"use client";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { CircleAlert } from "lucide-react";
import { MAINTENANCE_ATTR } from "@/components/maintenance/flag";

interface MaintenanceDialogProps {
  /** Customer-facing sentence from the API (already defaulted server-side). */
  message: string;
  /** Seconds the API asked us to wait. Already clamped to 15..300. */
  retryAfterS: number;
  /** Seconds until the window closes, or null when open-ended. */
  endsInS: number | null;
}

// Props are scalars, never the whole MaintenanceState. `reason` is an operator
// slug ("migration-007") that the API documents as reaching end users but only
// as a machine tag — keeping it out of this component's props makes leaking it
// into the HTML structurally impossible rather than a matter of discipline.

// Auto-retry bounds. The jitter is not cosmetic: without it every client in the
// world reloads on the same second the window lifts, and a just-recovered API
// takes the whole fleet at once.
const RETRY_MIN_MS = 15_000;
const RETRY_MAX_MS = 120_000;
const JITTER = 0.2;
const MAX_AUTO_RELOADS = 20;
const ATTEMPTS_KEY = "sticko:maintenance-reloads";

function readAttempts(): number {
  try {
    return Number(window.sessionStorage.getItem(ATTEMPTS_KEY)) || 0;
  } catch {
    return 0;
  }
}

function bumpAttempts(next: number) {
  try {
    window.sessionStorage.setItem(ATTEMPTS_KEY, String(next));
  } catch {
    // ignore: sessionStorage may be disabled
  }
}

/**
 * A maintenance page is served with HTTP 200 (an RSC page cannot set a status),
 * and serwist's catch-all NetworkFirst rule caches 200 documents for 24h. That
 * entry is only ever served when the network is unreachable, but there is no
 * reason to leave it lying around — drop the HTML caches before reloading.
 * Never touches the precache or the static-asset caches.
 */
async function purgeHtmlCaches() {
  if (typeof caches === "undefined") return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((k) => k.includes("pages") || k.includes("others")).map((k) => caches.delete(k)),
    );
  } catch {
    // ignore: Cache Storage may be unavailable (private mode, no SW)
  }
}

async function reloadNow() {
  await purgeHtmlCaches();
  window.location.reload();
}

// The attempt count only ever changes by way of a reload, which tears this
// component down — so there is nothing to subscribe to. useSyncExternalStore is
// still the right tool: it reads an external system (sessionStorage) during
// render with an explicit server snapshot, which is what keeps SSR and
// hydration honest without a setState-in-effect cascade.
const NEVER_CHANGES = () => () => {};

export function MaintenanceDialog({ message, retryAfterS, endsInS }: MaintenanceDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const gaveUp = useSyncExternalStore(
    NEVER_CHANGES,
    () => readAttempts() >= MAX_AUTO_RELOADS,
    () => false,
  );

  // Server-computed integer, so there is no Date, no timezone and no hydration
  // surface. Omitted entirely when the window is open-ended — better to say
  // nothing than to invent reassurance.
  const etaMinutes = endsInS !== null ? Math.max(1, Math.round(endsInS / 60)) : null;

  useEffect(() => {
    panelRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    // Bounded so an abandoned but visible tab cannot reload forever against an
    // origin that is already having a bad day.
    if (gaveUp) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const clear = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = () => {
      if (stopped || timer || document.hidden) return;
      // Never wait longer than the window itself is expected to last.
      const baseS = endsInS !== null ? Math.min(retryAfterS, endsInS) : retryAfterS;
      const clamped = Math.min(RETRY_MAX_MS, Math.max(RETRY_MIN_MS, baseS * 1000));
      const delay = clamped * (1 + (Math.random() * 2 - 1) * JITTER);
      timer = setTimeout(() => {
        timer = null;
        bumpAttempts(readAttempts() + 1);
        void reloadNow();
      }, delay);
    };

    // A backgrounded tab must not keep hammering the origin; it re-arms the
    // moment the visitor comes back.
    const onVisibility = () => (document.hidden ? clear() : schedule());

    schedule();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [retryAfterS, endsInS, gaveUp]);

  // This IS a permanent full-viewport overlay, and it WILL stop AdSense Auto
  // Ads from placing side rails (see the comments in AppPromptSheet.tsx and
  // SidebarNav.tsx explaining why nothing else in this app does that). It is
  // intended here: there is no publisher content behind it to monetise, and
  // AdSenseScript is suppressed on this render anyway.
  //
  // Non-dismissable by design, matching AdBlockDialog: no Escape handler, no
  // backdrop click, no focus trap (there is a single focusable element).
  return (
    <div
      {...{ [MAINTENANCE_ATTR]: "" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="maintenance-title"
      aria-describedby="maintenance-body"
      data-track-view="maintenance_dialog"
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="rounded-card w-full max-w-100 bg-white p-8 text-center shadow-(--shadow-card) outline-none"
      >
        <div className="flex justify-center">
          <CircleAlert aria-hidden className="text-brand-500 h-12 w-12" strokeWidth={1.6} />
        </div>

        <h2 id="maintenance-title" className="text-foreground mt-4 text-[20px] font-bold">
          We&apos;ll be right back
        </h2>
        <p id="maintenance-body" className="text-muted-foreground mt-3 text-[15px] leading-snug">
          {message}
        </p>
        {etaMinutes !== null ? (
          <p className="text-muted-foreground mt-2 text-[13px]">
            Expected back in about {etaMinutes} minute{etaMinutes === 1 ? "" : "s"}.
          </p>
        ) : null}
        <p className="text-muted-foreground mt-2 text-[13px]">
          {gaveUp
            ? "We stopped checking automatically. Tap below when you're ready to retry."
            : "This page will refresh itself as soon as we're back."}
        </p>

        <button
          type="button"
          onClick={() => void reloadNow()}
          className="rounded-pill bg-brand-500 hover:bg-brand-600 mt-6 w-full px-5 py-3 font-medium text-white transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
